import { useCallback, useEffect, useState } from "react";
import { Copy, Globe, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { useParams } from "react-router-dom";

import { toast } from "@/shared/hooks/use-toast";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { logger } from "@/shared/lib/logger";
import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Skeleton } from "@/shared/ui/skeleton";
import { cn } from "@/shared/lib/utils";

import {
  websiteService,
  type DomainCard,
  type DomainOverview,
  type SiteDetail,
} from "../api/websiteService";
import {
  domainStatusLabel,
  HOST_ICP_NOTE,
  hostCertText,
  isHostDomain,
  isSimulatedPublish,
  previewAddress,
  SIMULATED_NOTE,
  statusAdvanced,
} from "../model/domainStatus";
import { BindDomainForm } from "./components/BindDomainForm";
import { copyText } from "./components/copyText";
import { CustomDomainCard } from "./components/CustomDomainCard";
import { DomainAside } from "./components/DomainAside";
import { PreviewLink } from "./components/PreviewLink";
import { ToneBadge } from "./components/ToneBadge";
import { useCanManageSite } from "./hooks/useCanManageSite";
import { SiteShell } from "./SiteShell";

const errorMessage = (e: unknown) => (e instanceof Error ? e.message : String(e));
const httpStatus = (e: unknown) => (e as { httpStatus?: unknown } | null)?.httpStatus;

/** 一个站点一个主域名(api:站点首个域名 is_primary);页面只管主域名那张卡。 */
function primaryOf(o: DomainOverview): DomainCard | null {
  return o.domains.find((d) => d.isPrimary) ?? o.domains[0] ?? null;
}

/**
 * 屏 5「域名绑定」:左 = 平台预览域名卡 + 自有域名(没有 → 填写域名表单;有 → 四步 + DNS 表);
 * 右 = 域名状态链 + 备案提醒 + 去哪里改 DNS;页头 = 立即检测(有自有域名才出)+ 查看站点。
 *
 * ★ 状态全读 api 的 `DomainCard`:「立即检测」→ `POST …/check` 回来的卡**整张替换**本地那张,
 *   状态没往前走就把 `lastCheckNote` 亮成 toast(用户点了一下,得知道为什么还没好)。429 / 409 按原话 toast。
 * ★ 30 秒频控(附录 A.37):频控内 api 直接回上次的结果 —— `lastCheckedAt` 没变就说「30 秒内只检测一次,
 *   下面是上次结果」,不把旧结果说成「这次检测没变化」。已上线的站再检测一次还是 live → 「已上线,一切正常」。
 * ★ 预览域名「已生效」只对已发布的站成立:草稿站写「发布后生效」、「查看站点」上锁(与站点卡同口径),
 *   不给一个还没内容的地址挂「已生效 · HTTPS」。
 * ★ 绑定 / 检测 / 解绑之后顺手重拉站点:五步条的「发布」一格看的是 `site.primaryDomain` 是否 live。
 * ★ 模拟发布(附录 A.38 / A.40③):整页不出现「证书 / HTTPS」,预览卡写「已生效(模拟发布)」,自有域名卡 / 状态链 /
 *   四步 / 填写域名表单跟着换说法。信号先看 `overview.publisherMode`(网关当前模式 —— 从没发布过的草稿站也有),
 *   老后端没给才退到 `site.publisherMode`(见 `isSimulatedPublish`)。
 * ★ 预览卡按 `previewAddress` 三态画(0925 A12-2):host 模式泛解析没开前,平台子域 `{slug}.site.xbwhynot.com` 打不开,
 *   不显示它;已发布显示宿主 `?siteId=` 地址,没发布写「发布后生成预览地址」,自有域名上线后显示自有域名。
 * ★ host 模式(A.54):顶部提示大陆节点要备案;绑定表单多「备案号(选填)」;状态链「签发证书」一节挂宿主签发状态人话
 *   (`hostCert`);站点从没发布过(宿主上还没有这个站)时绑定按钮禁用并说「先发布一次」。
 * ★ STAFF 看不到绑定 / 解绑(A.59,api 只给 TENANT_MANAGER+);「立即检测」照旧。
 */
export default function DomainPage() {
  const { id = "" } = useParams<{ id: string }>();
  const confirm = useConfirm();
  const canManage = useCanManageSite();
  const [site, setSite] = useState<SiteDetail | null>(null);
  const [overview, setOverview] = useState<DomainOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [unbinding, setUnbinding] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [s, o] = await Promise.all([websiteService.getSite(id), websiteService.domains(id)]);
      setSite(s);
      setOverview(o);
    } catch (e) {
      setError(errorMessage(e));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshSite = async () => {
    try {
      setSite(await websiteService.getSite(id));
    } catch (e) {
      logger.warn("[website] 域名变更后重拉站点失败", { error: String(e) });
    }
  };

  /** api 回来的卡整张换进去(没有就追加);不在本地拼状态。 */
  const putCard = (card: DomainCard) =>
    setOverview((o) =>
      o
        ? {
            ...o,
            domains: o.domains.some((d) => d.id === card.id)
              ? o.domains.map((d) => (d.id === card.id ? card : d))
              : [...o.domains, card],
          }
        : o
    );

  if (error) {
    return <QueryErrorBanner isError message={error} onRetry={() => void load()} />;
  }
  if (!site || !overview) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-2/3" />
        <Skeleton className="h-6 w-1/2" />
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-4">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  const custom = primaryOf(overview);
  const simulated = isSimulatedPublish(overview, site);
  const host = isHostDomain(overview, site);
  const hostNote = host
    ? hostCertText(custom?.hostCert ?? overview.hostCert, custom?.lastCheckNote)
    : null;
  /** host 模式下宿主上还没有这个站(没发布过)时不能绑(A.54 → 409)。 */
  const bindBlocked = host && site.currentVersionNo == null ? "先发布一次,再绑定自有域名" : null;
  const preview = previewAddress(overview, site, custom);
  const previewUrl = preview.url ?? overview.previewUrl ?? site.previewUrl;
  const archived = site.status === "archived";
  // 1010:一键生成发了预览(有版本号)预览地址就已生效,不必等正式上线(launched)
  const previewLive = !archived && (site.currentVersionNo != null || site.status === "published");
  const previewState = previewLive ? "已生效" : archived ? "已下线" : "发布后生效";
  /** 预览地址点不开的原因(null = 能开)。 */
  const previewBlocked =
    previewLive && preview.url ? null : archived ? "站点已归档,预览地址已下线" : "发布后可预览";
  /** 卡的名字:host 模式下泛解析没开,平台子域打不开,说「预览地址」而不是「平台预览域名」。 */
  const previewTitle = host ? "预览地址" : "平台预览域名";

  const check = async () => {
    if (!custom) return;
    const prev = custom.overallStatus;
    const prevCheckedAt = custom.lastCheckedAt;
    setChecking(true);
    try {
      const next = await websiteService.checkDomain(id, custom.id);
      putCard(next);
      const label = domainStatusLabel(next.overallStatus, simulated);
      if (next.lastCheckedAt != null && next.lastCheckedAt === prevCheckedAt) {
        toast({
          title: "30 秒内只检测一次,下面是上次结果",
          description: next.lastCheckNote ?? undefined,
        });
      } else if (next.overallStatus === "error") {
        toast({
          title: "检测发现问题",
          description: next.lastCheckNote ?? label,
          variant: "destructive",
        });
      } else if (statusAdvanced(prev, next.overallStatus)) {
        toast({ title: `检测完成 · ${label}`, description: next.lastCheckNote ?? undefined });
      } else if (next.overallStatus === "live") {
        toast({ title: "已上线,一切正常", description: next.lastCheckNote ?? undefined });
      } else {
        toast({
          title: `还在「${label}」`,
          description: next.lastCheckNote ?? "这次检测状态没有变化",
        });
      }
      if (next.overallStatus !== prev) void refreshSite();
    } catch (e) {
      const status = httpStatus(e);
      toast({
        title:
          status === 429
            ? "检测太频繁,稍等半分钟再点"
            : status === 409
              ? "现在不能检测"
              : "检测失败",
        description: errorMessage(e),
        variant: "destructive",
      });
    } finally {
      setChecking(false);
    }
  };

  const unbind = async () => {
    if (!custom) return;
    const ok = await confirm({
      title: `解绑 ${custom.domain}?`,
      description: "解绑后这个域名不再指向本站,DNS 里为它加的记录可以删掉;平台预览域名不受影响。",
      confirmText: "解绑",
      variant: "destructive",
    });
    if (!ok) return;
    setUnbinding(true);
    try {
      await websiteService.unbindDomain(id, custom.id);
      setOverview((o) => (o ? { ...o, domains: o.domains.filter((d) => d.id !== custom.id) } : o));
      toast({ title: `已解绑 ${custom.domain}` });
      void refreshSite();
    } catch (e) {
      toast({
        title: httpStatus(e) === 403 ? "没有解绑权限" : "解绑失败",
        description: errorMessage(e),
        variant: "destructive",
      });
    } finally {
      setUnbinding(false);
    }
  };

  const subtitle = `${previewTitle}${previewState} · ${
    custom
      ? `自有域名 ${custom.domain} ${domainStatusLabel(custom.overallStatus, simulated)}`
      : "还没有绑定自有域名"
  }`;

  return (
    <SiteShell
      site={site}
      pageTitle="域名绑定"
      subtitle={subtitle}
      actions={
        <>
          {custom && (
            <Button
              variant="outline"
              size="sm"
              disabled={checking || unbinding}
              onClick={() => void check()}
            >
              {checking ? <Loader2 className="animate-spin" /> : <RefreshCw />}
              立即检测
            </Button>
          )}
          <PreviewLink href={previewUrl} blocked={previewBlocked} label="查看站点" solid />
        </>
      }
    >
      {(host || overview.icpRequired) && (
        <p
          role="note"
          className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-foreground"
        >
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
          {HOST_ICP_NOTE}
        </p>
      )}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-4">
          <section
            aria-label={previewTitle}
            className={cn(
              "flex flex-wrap items-start justify-between gap-3 border border-border bg-card p-4 text-card-foreground",
              CARD_WALL_RADIUS
            )}
          >
            <div className="min-w-0 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <Globe className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                <span
                  data-testid="preview-address"
                  className="break-all font-mono text-base font-semibold text-foreground"
                >
                  {preview.address ?? "发布后生成预览地址"}
                </span>
                <ToneBadge tone={previewLive && preview.url ? "ok" : "muted"}>
                  {previewLive && preview.url
                    ? simulated
                      ? `已生效${SIMULATED_NOTE}`
                      : "已生效 · HTTPS"
                    : previewState}
                </ToneBadge>
                <ToneBadge tone="muted">
                  {preview.kind === "custom"
                    ? "自有域名"
                    : preview.kind === "subdomain"
                      ? "平台预览域名 · 免费"
                      : "宿主预览地址 · 免费"}
                </ToneBadge>
              </div>
              <p className="text-xs text-muted-foreground">
                {preview.kind === "custom"
                  ? "自有域名已上线,对外用它。"
                  : host && preview.kind !== "subdomain"
                    ? "站点子域名解析还没开通,发布后先用宿主预览地址访问;自有域名上线后这里换成自有域名。"
                    : "发布即生效,不用备案;自有域名没配好之前对外先用它。"}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!preview.url}
                onClick={() => void copyText(previewUrl, "预览地址")}
              >
                <Copy />
                复制
              </Button>
              <PreviewLink href={previewUrl} blocked={previewBlocked} label="打开" />
            </div>
          </section>

          {custom ? (
            <CustomDomainCard
              card={custom}
              gatewayHost={overview.gatewayHost}
              simulated={simulated}
              hostNote={hostNote}
              canUnbind={canManage}
              busy={checking || unbinding}
              unbinding={unbinding}
              onUnbind={() => void unbind()}
            />
          ) : canManage ? (
            <BindDomainForm
              siteId={id}
              simulated={simulated}
              showIcp={host}
              blockedReason={bindBlocked}
              onBound={(card) => {
                putCard(card);
                void refreshSite();
              }}
            />
          ) : (
            <section
              aria-label="自有域名"
              className={cn(
                "border border-border bg-card p-4 text-xs text-muted-foreground",
                CARD_WALL_RADIUS
              )}
            >
              <h2 className="mb-1 text-sm font-semibold text-foreground">自有域名</h2>
              还没有绑定自有域名。绑定 / 解绑需要管理员或经理操作。
            </section>
          )}
        </div>

        <DomainAside card={custom} simulated={simulated} hostNote={hostNote} />
      </div>
    </SiteShell>
  );
}
